/**
 * SortOptionsListContainer - Manages expand/collapse state for sort options
 *
 * Provides collapsible dropdown behavior for the mobile filter modal context.
 * Tapping the trigger expands the options list; selecting an option or tapping
 * the trigger again collapses it.
 * @remarks This is a Container component - handles expand/collapse state
 */

import React, { useCallback, useMemo, useState } from "react";

import SortOptionsListView from "./SortOptionsListView";

import {
  SimilarPlayersSortOption,
  SortFieldCallbackMap,
} from "@/features/player-detail/types";

import { SimilarPlayerSortField, SortOrder } from "@/generated/graphql";

/**
 * Props for SortOptionsListContainer component
 */
interface SortOptionsListContainerProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Available sort options */
  readonly sortOptions: readonly SimilarPlayersSortOption[];
  /** Currently selected sort field */
  readonly selectedSortField: SimilarPlayerSortField;
  /** Display label for selected sort option */
  readonly selectedSortLabel: string;
  /** Current sort order */
  readonly sortOrder: SortOrder;
  /** Pre-bound callbacks for each sort field that also close the parent menu */
  readonly sortFieldCallbacks: SortFieldCallbackMap;
  /** Callback to toggle sort order */
  readonly onSortOrderToggle: () => void;
}

/**
 * Container that wraps sort options list with expand/collapse behavior
 * @param props - Container props
 * @param props.isDark - Whether dark mode is active
 * @param props.sortOptions - Available sort options
 * @param props.selectedSortField - Currently selected sort field
 * @param props.selectedSortLabel - Display label for selected sort option
 * @param props.sortOrder - Current sort order
 * @param props.sortFieldCallbacks - Pre-bound callbacks for each sort field
 * @param props.onSortOrderToggle - Callback to toggle sort order
 * @returns SortOptionsListView with expand/collapse state
 */
const SortOptionsListContainer = ({
  isDark,
  sortOptions,
  selectedSortField,
  selectedSortLabel,
  sortOrder,
  sortFieldCallbacks,
  onSortOrderToggle,
}: SortOptionsListContainerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  /**
   * Wraps each sort field callback to also collapse the list after selection.
   * Uses reduce to build a record keyed by sort field.
   */
  const wrappedCallbacks = useMemo<SortFieldCallbackMap>(
    () =>
      sortOptions.reduce(
        (acc, option) => ({
          ...acc,
          [option.field]: () => {
            sortFieldCallbacks[option.field]?.();
            setIsExpanded(false);
          },
        }),
        {} as SortFieldCallbackMap
      ),
    [sortOptions, sortFieldCallbacks]
  );

  return (
    <SortOptionsListView
      isDark={isDark}
      sortOptions={sortOptions}
      selectedSortField={selectedSortField}
      selectedSortLabel={selectedSortLabel}
      sortOrder={sortOrder}
      sortFieldCallbacks={wrappedCallbacks}
      onSortOrderToggle={onSortOrderToggle}
      isExpanded={isExpanded}
      onToggleExpanded={handleToggleExpanded}
    />
  );
};

SortOptionsListContainer.displayName = "SortOptionsListContainer";

export default React.memo(SortOptionsListContainer);
